/**
 * 기관 담당자 포털의 탭 카탈로그.
 *
 * 백엔드 `src/user/institution-tab.ts` 와 키가 1:1로 맞아야 한다. 한쪽만
 * 고치면 관리자가 켠 탭이 화면에 없거나, 화면에는 보이는데 서버가 403 을
 * 주는 상태가 된다.
 *
 * 이 파일은 관리자 화면(권한 편집기)과 기관 포털(사이드바) **양쪽**이 읽는
 * 유일한 공유 지점이다. 포털 화면 코드 자체는 `src/portal/` 아래에만 두고
 * 관리자 번들과 섞지 않는다.
 */
export const INSTITUTION_TAB_KEYS = [
  'overview',
  'report',
  'mindrest',
  'aicounsel',
  'aidiary',
  'mindcounsel',
  'members',
  'analytics',
  'rppg',
  'moments',
  'kiosks',
  'wifi',
  'exports',
  'inquiries',
  'complaints',
] as const;

export type InstitutionTabKey = (typeof INSTITUTION_TAB_KEYS)[number];

/**
 * 계정 권한이 아니라 **기관 콘텐츠 배정**으로 열리는 탭.
 *
 * 백엔드 `src/user/institution-tab.ts` 의 `CONTENT_GATED_TABS` 와 같은 값이다.
 * 관리자가 기관 관리의 "배정 콘텐츠" 에서 그 콘텐츠를 켜면 키오스크 카드와 이
 * 탭이 함께 열린다 (민원 계약 §8).
 *
 * 화면은 이 목록을 **판정에 쓰지 않는다** — 서버가 `/institution-portal/me` 의
 * `allowedTabs` 에 이미 결과를 얹어 주므로 `hasTab()` 이 그대로 동작한다.
 * 여기 두는 이유는 관리자 탭 권한 편집기에서 "이 줄은 여기서 못 켠다" 를
 * 표시하기 위해서다.
 */
export const CONTENT_GATED_TABS: Readonly<
  Partial<Record<InstitutionTabKey, string>>
> = {
  complaints: 'civil-complaint',
  // 마음쉼터는 홈 카드 하나(`mind-rest`)가 스위치다. 콘텐츠 4개를 각각 보지
  // 않는다 — 넷 중 셋만 배정된 기관에서 탭이 반쯤 열린 상태를 만들지 않기 위해서다.
  mindrest: 'mind-rest',
  // 마음 여행·AI 일기는 홈 카드 하나씩이 각자의 스위치다(ai-content-report-contract.md §5)
  aicounsel: 'ai-counsel',
  aidiary: 'ai-diary',
  mindcounsel: 'mind-counsel',
};

/** 관리자 탭 권한 편집기에서 잠긴 줄에 적을 이유 */
export const CONTENT_GATED_TAB_REASON =
  '기관 관리의 배정 콘텐츠로 열립니다. 여기서는 바꿀 수 없습니다.';

/**
 * 권한과 무관하게 항상 열려 있는 탭.
 *
 * `overview` 는 승인된 계정의 착지 화면이고, `inquiries` 는 지원 창구다 —
 * 무엇이 안 보인다고 물어볼 통로까지 권한으로 잠기면 담당자가 막히기 때문에
 * 문의는 권한 설정과 무관하게 열어 둔다.
 */
export const ALWAYS_ALLOWED_TABS: readonly InstitutionTabKey[] = ['overview', 'inquiries'];

/**
 * 마음 상담 콘텐츠의 화면 이름 — **이름은 바뀔 수 있다**(mind-counsel-contract.md 머리말).
 *
 * 탭 라벨·절 제목·빈 상태 문구·도움말이 모두 이 값을 읽는다. 코드 식별자
 * (`mindcounsel`, `mindCounsel`, `MindCounsel*`)는 이름이 바뀌어도 그대로 둔다.
 */
export const MIND_COUNSEL_LABEL = '마음 상담';

export interface InstitutionTabMeta {
  key: InstitutionTabKey;
  label: string;
  description: string;
  /** 포털 내부 경로 (`/portal` 하위) */
  path: string;
}

export const INSTITUTION_TABS: readonly InstitutionTabMeta[] = [
  {
    key: 'overview',
    label: '기관 현황',
    description: '배정된 기관의 요약 지표. 승인된 계정이면 항상 열립니다.',
    path: '/portal',
  },
  {
    key: 'report',
    label: '감정·건강 리포트',
    description: '감정 온도·스트레스·연령대 분포 등 기관 단위 집계.',
    path: '/portal/report',
  },
  {
    key: 'mindrest',
    label: '마음쉼터 리포트',
    description: '마음쉼터 콘텐츠별 이용 현황과 이용 전후 변화.',
    path: '/portal/mindrest',
  },
  {
    key: 'aicounsel',
    label: '마음 여행',
    description:
      'AI 상담 마음 여행의 이용·완료율, PHQ-9 구간, 해 본 콘텐츠와 회원별 요약. 기관 관리의 배정 콘텐츠로 열립니다.',
    path: '/portal/aicounsel',
  },
  {
    key: 'aidiary',
    label: 'AI 일기',
    description:
      'AI 일기 작성·완성률, 기분 단어 분포와 회원별 요약. 기관 관리의 배정 콘텐츠로 열립니다.',
    path: '/portal/aidiary',
  },
  {
    key: 'mindcounsel',
    label: MIND_COUNSEL_LABEL,
    description: `${MIND_COUNSEL_LABEL}의 이용·완료율, 마음 날씨, 살펴볼 점, 대화 흐름과 회차별 마음 리포트. 기관 관리의 배정 콘텐츠로 열립니다.`,
    path: '/portal/mindcounsel',
  },
  {
    key: 'members',
    label: '이용자 현황',
    description: '기관 소속 이용자 목록과 개인별 리포트(마스킹 표기).',
    path: '/portal/members',
  },
  {
    key: 'analytics',
    label: '이용 통계',
    description: 'DAU/MAU, 요일·시간대 이용 분포 등 이용량 지표.',
    path: '/portal/analytics',
  },
  {
    key: 'rppg',
    label: 'rPPG 측정 기록',
    description: '심박·HRV·스트레스 측정 세션과 요약값.',
    path: '/portal/rppg',
  },
  {
    key: 'moments',
    label: '순간기록',
    description: '기관 소속 키오스크에서 수집된 순간기록 열람.',
    path: '/portal/moments',
  },
  {
    key: 'kiosks',
    label: '기기 상태',
    description: '배정된 키오스크의 온·오프라인 상태 (조회 전용).',
    path: '/portal/kiosks',
  },
  {
    key: 'wifi',
    label: 'WiFi 설정 파일',
    description:
      '키오스크에 USB 로 적용할 WiFi 설정 파일을 만듭니다. 브라우저에서 파일만 만들어지며 서버에 저장되는 값은 없습니다.',
    path: '/portal/wifi',
  },
  {
    key: 'exports',
    label: '보고서 출력',
    description: '기간을 지정해 기관 리포트 PDF 를 내려받습니다.',
    path: '/portal/exports',
  },
  {
    key: 'inquiries',
    label: '문의하기',
    description:
      '관리자에게 문의를 남기고 답변을 주고받습니다. 승인된 계정이면 항상 열립니다.',
    path: '/portal/inquiries',
  },
  {
    key: 'complaints',
    label: '민원·문의 접수',
    description:
      '키오스크 이용자가 남긴 민원·문의를 확인하고 답변합니다. 계정 권한이 아니라 기관 관리의 배정 콘텐츠로 열립니다.',
    path: '/portal/complaints',
  },
] as const;

export const INSTITUTION_TAB_BY_KEY: Record<InstitutionTabKey, InstitutionTabMeta> =
  Object.fromEntries(INSTITUTION_TABS.map((tab) => [tab.key, tab])) as Record<
    InstitutionTabKey,
    InstitutionTabMeta
  >;

export function isInstitutionTabKey(value: unknown): value is InstitutionTabKey {
  return (
    typeof value === 'string' &&
    (INSTITUTION_TAB_KEYS as readonly string[]).includes(value)
  );
}

/**
 * 서버가 준 권한 배열을 카탈로그 순서로 정규화한다.
 * 모르는 키는 버리고, 항상 열려 있는 탭은 없더라도 채운다.
 */
export function normalizeTabs(value: unknown): InstitutionTabKey[] {
  const seen = new Set<InstitutionTabKey>();
  if (Array.isArray(value)) {
    for (const item of value) {
      if (isInstitutionTabKey(item)) seen.add(item);
    }
  }
  for (const always of ALWAYS_ALLOWED_TABS) seen.add(always);
  return INSTITUTION_TAB_KEYS.filter((key) => seen.has(key));
}

export function hasTab(
  allowed: readonly InstitutionTabKey[] | null | undefined,
  key: InstitutionTabKey,
): boolean {
  if (ALWAYS_ALLOWED_TABS.includes(key)) return true;
  return !!allowed && allowed.includes(key);
}
